/**
 * <bm-facet-carat-slider>
 *
 * Dual-thumb range slider that drives the hidden "Total carat weight" checkbox
 * list in the collection facet drawer. See `snippets/bm-facet-drawer-carat-slider.liquid`
 * for the markup contract.
 *
 * Data flow:
 *   - Hidden <input type="checkbox" data-bm-carat-value> elements inside the
 *     component hold the numeric values exposed by Shopify's `filter.values`
 *     and remain inside the parent `<form is="facet-form">` for FormData
 *     serialisation.
 *   - Two <input type="range"> inputs (min / max) are stacked via CSS. This
 *     class reads/writes their `.value` as *indices* into the sorted value
 *     array — the hidden checkboxes keep the original numeric strings, so no
 *     `parseFloat` precision drift leaks into the URL.
 *   - On any thumb move we:
 *       1. Clamp `min <= max` (and nudge the other thumb if they cross).
 *       2. Update the track fill + the min/max display copies.
 *       3. Toggle `.checked` on every hidden checkbox whose sorted index lies
 *          within [min, max]. Disabled (`count == 0`) values stay unchecked.
 *       4. Fire a bubbling `change` event from the first mutated checkbox so
 *          `FacetForm` runs its existing submit pipeline (`assets/facets.js`).
 *
 * This keeps URL + AJAX semantics identical to the original checkbox list — we
 * just hide the checkboxes behind a friendlier affordance.
 */

/* Bonded Matter — collection facet drawer Carat range slider. */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('bm-facet-carat-slider')) return;

  class BmFacetCaratSlider extends HTMLElement {
    constructor() {
      super();
      this._bound = false;
      this._onRangeInput = this._onRangeInput.bind(this);
      this._onRangeChange = this._onRangeChange.bind(this);
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.minRange = this.querySelector('[data-bm-carat-range-min]');
      this.maxRange = this.querySelector('[data-bm-carat-range-max]');
      this.fill = this.querySelector('[data-bm-carat-fill]');
      this.displayMin = this.querySelector('[data-bm-carat-display-min]');
      this.displayMax = this.querySelector('[data-bm-carat-display-max]');
      this.checkboxes = Array.from(
        this.querySelectorAll('input[type="checkbox"][data-bm-carat-value]'),
      );

      if (!this.minRange || !this.maxRange || this.checkboxes.length === 0) {
        return;
      }

      this.checkboxes.sort((a, b) => {
        const ai = parseInt(a.dataset.bmCaratIndex || '0', 10);
        const bi = parseInt(b.dataset.bmCaratIndex || '0', 10);
        return ai - bi;
      });

      this.maxIndex = this.checkboxes.length - 1;
      this.minRange.min = '0';
      this.minRange.max = String(this.maxIndex);
      this.maxRange.min = '0';
      this.maxRange.max = String(this.maxIndex);

      this._guardInitialValues();

      this.minRange.addEventListener('input', this._onRangeInput);
      this.maxRange.addEventListener('input', this._onRangeInput);
      this.minRange.addEventListener('change', this._onRangeChange);
      this.maxRange.addEventListener('change', this._onRangeChange);

      this._paint();
    }

    disconnectedCallback() {
      if (!this._bound) return;
      if (this.minRange) {
        this.minRange.removeEventListener('input', this._onRangeInput);
        this.minRange.removeEventListener('change', this._onRangeChange);
      }
      if (this.maxRange) {
        this.maxRange.removeEventListener('input', this._onRangeInput);
        this.maxRange.removeEventListener('change', this._onRangeChange);
      }
      this._bound = false;
    }

    _guardInitialValues() {
      const rawMin = parseInt(this.minRange.value, 10);
      const rawMax = parseInt(this.maxRange.value, 10);
      const safeMin = Number.isFinite(rawMin) ? this._clamp(rawMin) : 0;
      const safeMax = Number.isFinite(rawMax) ? this._clamp(rawMax) : this.maxIndex;
      this.minRange.value = String(Math.min(safeMin, safeMax));
      this.maxRange.value = String(Math.max(safeMin, safeMax));
    }

    _clamp(i) {
      if (i < 0) return 0;
      if (i > this.maxIndex) return this.maxIndex;
      return i;
    }

    _onRangeInput(event) {
      let lo = parseInt(this.minRange.value, 10);
      let hi = parseInt(this.maxRange.value, 10);
      if (!Number.isFinite(lo)) lo = 0;
      if (!Number.isFinite(hi)) hi = this.maxIndex;

      if (event.target === this.minRange && lo > hi) {
        hi = lo;
        this.maxRange.value = String(hi);
      } else if (event.target === this.maxRange && hi < lo) {
        lo = hi;
        this.minRange.value = String(lo);
      }
      this._paint(lo, hi);
    }

    _onRangeChange() {
      let lo = parseInt(this.minRange.value, 10);
      let hi = parseInt(this.maxRange.value, 10);
      if (!Number.isFinite(lo)) lo = 0;
      if (!Number.isFinite(hi)) hi = this.maxIndex;
      this._applyRangeToCheckboxes(lo, hi);
    }

    _paint(lo, hi) {
      if (typeof lo !== 'number') {
        lo = parseInt(this.minRange.value, 10);
        if (!Number.isFinite(lo)) lo = 0;
      }
      if (typeof hi !== 'number') {
        hi = parseInt(this.maxRange.value, 10);
        if (!Number.isFinite(hi)) hi = this.maxIndex;
      }

      const denom = this.maxIndex > 0 ? this.maxIndex : 1;
      const loPct = (lo / denom) * 100;
      const hiPct = (hi / denom) * 100;

      if (this.fill) {
        this.fill.style.left = loPct + '%';
        this.fill.style.right = 100 - hiPct + '%';
      }

      const loBox = this.checkboxes[lo];
      const hiBox = this.checkboxes[hi];
      if (this.displayMin && loBox) {
        this.displayMin.textContent =
          loBox.dataset.bmCaratLabel || loBox.dataset.bmCaratValue || '';
      }
      if (this.displayMax && hiBox) {
        this.displayMax.textContent =
          hiBox.dataset.bmCaratLabel || hiBox.dataset.bmCaratValue || '';
      }
    }

    _applyRangeToCheckboxes(lo, hi) {
      let changed = false;
      let firstMutated = null;

      for (let i = 0; i < this.checkboxes.length; i += 1) {
        const cb = this.checkboxes[i];
        if (cb.disabled) continue;
        const shouldCheck = i >= lo && i <= hi;
        const fullRange = lo === 0 && hi === this.maxIndex;
        // When the user has not narrowed the range, submit empty (the default
        // "all values" state) instead of serialising every single checkbox —
        // keeps the URL clean and matches the legacy "nothing selected" UX.
        const next = fullRange ? false : shouldCheck;
        if (cb.checked !== next) {
          cb.checked = next;
          if (!firstMutated) firstMutated = cb;
          changed = true;
        }
      }

      if (!changed) return;
      const target = firstMutated || this.checkboxes[0];
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  window.customElements.define('bm-facet-carat-slider', BmFacetCaratSlider);
})();
